import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

/**
 * One alert card.
 *
 * Owns its own dismissal timer rather than having the stack run a single
 * sweep: the timer has to pause while the pointer is over the card, and that
 * is a property of this card, not of the stack. A toast that vanishes from
 * under the cursor mid-read is the fastest way to make someone stop trusting
 * them.
 *
 * Leaving is animated rather than instant, so a card that goes while another
 * is arriving does not make the rest of the stack jump.
 */
const LEAVE_MS = 200;

const AlertToast = ({ alert, onOpen, onDismiss }) => {
    const [leaving, setLeaving] = useState(false);
    const timer = useRef(null);
    const leaveTimer = useRef(null);

    const close = () => {
        if (leaving) return;
        setLeaving(true);
        leaveTimer.current = setTimeout(() => onDismiss(alert.id), LEAVE_MS);
    };

    const start = () => {
        clearTimeout(timer.current);
        if (!alert.ttl) return;
        timer.current = setTimeout(close, alert.ttl);
    };

    useEffect(() => {
        start();
        return () => {
            clearTimeout(timer.current);
            clearTimeout(leaveTimer.current);
        };
        // The card is keyed by id in the stack, so this runs once per alert.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const open = () => {
        clearTimeout(timer.current);
        onOpen(alert);
        onDismiss(alert.id);
    };

    return (
        <div
            className={`app-alert tone-${alert.tone}${leaving ? ' is-leaving' : ''}`}
            role="status"
            onMouseEnter={() => clearTimeout(timer.current)}
            onMouseLeave={start}
        >
            <span className="app-alert-icon" aria-hidden="true">
                <FontAwesomeIcon icon={alert.icon} />
            </span>

            {/*
              * A button rather than a link: `link` is an in-app route and this
              * has to go through the router, not reload the shell. The whole
              * body is the target so the card can be tapped anywhere on a
              * phone, which is where these matter most.
              */}
            <button type="button" className="app-alert-body" onClick={open}>
                <strong className="app-alert-title">{alert.title}</strong>
                {alert.message && (
                    <span className="app-alert-message">{alert.message}</span>
                )}
            </button>

            <button
                type="button"
                className="app-alert-close"
                onClick={close}
                aria-label="Dismiss notification"
            >
                <FontAwesomeIcon icon={faTimes} />
            </button>
        </div>
    );
};

export default AlertToast;
